import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IUser, ITwoFactorSetupStatusItem } from '@data/schema';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../services/user.service';
import { StorageService } from '../platform/storage.service';
import { TwoFactorReminderModalComponent } from '@shared/modals/two-factor-reminder-modal/two-factor-reminder-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, take } from 'rxjs';
import { environment } from '@env';

interface ITwoFactorReminderState {
  closed: boolean;
  loginCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class TwoFactorReminderService {
  private readonly userService = inject(UserService);
  private readonly modalService = inject(BsModalService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  private readonly storageKeyPrefix = 'twoFactorReminder';

  handleAfterLogin(user: IUser): void {
    if (!environment.twoFactorAuth) {
      return;
    }

    this.userService
      .getTwoFactorSetupStatus()
      .pipe(
        take(1),
        catchError(() => EMPTY)
      )
      .subscribe((status) => {
        if (this.isAuthenticatorEnabled(status)) {
          return;
        }

        this.notifyAboutDisabledTwoFactor(user);
      });
  }

  private notifyAboutDisabledTwoFactor(user: IUser): void {
    const state = this.getState(user.id);
    const loginCount = state.loginCount + 1;

    if (!state.closed || loginCount % 4 === 0) {
      this.showReminderModal(user, loginCount);
      return;
    }

    this.saveState(user.id, {
      closed: true,
      loginCount,
    });
    this.toast.warning(this.translate.instant('two-factor-reminder.toast_message'), undefined, {
      positionClass: 'toast-bottom-right',
    });
  }

  private showReminderModal(user: IUser, loginCount: number): void {
    const modalRef = this.modalService.show(TwoFactorReminderModalComponent, {
      class: 'custom-size-modal-590',
      ignoreBackdropClick: true,
    });

    modalRef?.content?.action?.pipe(take(1)).subscribe((shouldOpenSettings: boolean) => {
      this.saveState(user.id, {
        closed: true,
        loginCount,
      });

      if (shouldOpenSettings) {
        this.router.navigate(['/user', user.id, 'account']);
      }
    });
  }

  private getState(userId: number): ITwoFactorReminderState {
    const rawState = StorageService.local.getItem(this.getStorageKey(userId));

    if (!rawState) {
      return {
        closed: false,
        loginCount: 0,
      };
    }

    try {
      const state = JSON.parse(rawState) as Partial<ITwoFactorReminderState>;
      const loginCount = Number(state.loginCount);

      return {
        closed: !!state.closed,
        loginCount: Number.isFinite(loginCount) ? loginCount : 0,
      };
    } catch {
      return {
        closed: false,
        loginCount: 0,
      };
    }
  }

  private saveState(userId: number, state: ITwoFactorReminderState): void {
    StorageService.local.setItem(this.getStorageKey(userId), JSON.stringify(state));
  }

  private getStorageKey(userId: number): string {
    return `${this.storageKeyPrefix}.${userId}`;
  }

  private isAuthenticatorEnabled(status: ITwoFactorSetupStatusItem[]): boolean {
    const authenticator = status.find((item) => {
      const normalizedType = `${item.type ?? ''}`.toLowerCase();

      return normalizedType.includes('authenticator') || normalizedType.includes('authenticationapp');
    });

    if (!authenticator) {
      return false;
    }

    const normalizedStatus = `${authenticator.status ?? ''}`.toLowerCase();

    return ['connected', 'enabled', 'active', 'confirmed', 'true', '1'].some(
      (enabledStatus) => normalizedStatus === enabledStatus || normalizedStatus.includes(enabledStatus)
    );
  }
}
